import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import DataGrid, {
  Column, SearchPanel, Export, Summary, TotalItem, Selection, Paging,
} from "devextreme-react/data-grid";
import { Button } from 'devextreme-react/button';
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import RadioGroup from "devextreme-react/radio-group";
import notify from 'devextreme/ui/notify';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from "exceljs";
import { saveAs } from 'file-saver';
import API_URL from '../config';

const binLimitOptions = [50, 100, 150, 200, 300, 500, 1000];

// [수정 1] user prop 추가
export default function WaferSummaryPage({ user }) {
  const [loading, setLoading] = useState(false);
  const [gridData, setGridData] = useState([]);
  
  // [수정 2] 고객사 권한 여부 체크
  const isCustFixed = user && user.role === 'CUST';

  const today = new Date();
  const [dateFrom, setDateFrom] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [dateTo, setDateTo] = useState(today);

  // [수정 3] 초기값 설정: CUST 권한이면 로그인 ID 할당
  const [custCd, setCustCd] = useState(isCustFixed ? user.username : null);
  const [deviCd, setDeviCd] = useState(null);
  const [operCd, setOperCd] = useState(null);
  
  const [binLimit, setBinLimit] = useState(50);

  const [custList, setCustList] = useState([]);
  const [deviList, setDeviList] = useState([]);
  const [operList, setOperList] = useState([]);

  const fmtDate = (d) => d ? d.toISOString().slice(0, 10) : '';

  // 1. 날짜 변경 -> 고객사
  useEffect(() => {
    // [수정 4] 고정된 사용자가 아닐 때만 null로 초기화
    if (!isCustFixed) {
      setCustCd(null);
    }
    setDeviCd(null); 
    setOperCd(null);
    setDeviList([{ CODE_CD: 'ALL' }]); 
    setOperList([{ CODE_CD: 'ALL' }]);

    axios.get(`${API_URL}/api/oracle/wafer-summary/combo/cust`, {
      params: { dateFrom: fmtDate(dateFrom), dateTo: fmtDate(dateTo) }
    }).then(res => {
      setCustList(res.data.data || []);
      // 목록 로드 후 확실하게 고정값 재설정 (안전장치)
      if (isCustFixed) {
        setCustCd(user.username);
      }
    }).catch(console.error);
  }, [dateFrom, dateTo, isCustFixed, user]);

  // 2. 고객사 -> Device
  useEffect(() => {
    if (!custCd) { setDeviList([{ CODE_CD: 'ALL' }]); return; }
    setDeviCd('ALL'); setOperCd('ALL');

    axios.get(`${API_URL}/api/oracle/wafer-summary/combo/device`, {
      params: { dateFrom: fmtDate(dateFrom), dateTo: fmtDate(dateTo), custCd }
    }).then(res => {
        const list = res.data.data || [];
        setDeviList([{ CODE_CD: 'ALL' }, ...list]);
    }).catch(console.error);
  }, [custCd, dateFrom, dateTo]);

  // 3. Device -> Oper
  useEffect(() => {
    if (!custCd) { setOperList([{ CODE_CD: 'ALL' }]); return; }
    setOperCd('ALL');
    
    const sendDevi = (deviCd === 'ALL' || !deviCd) ? '%' : deviCd;

    axios.get(`${API_URL}/api/oracle/wafer-summary/combo/oper`, {
      params: { dateFrom: fmtDate(dateFrom), dateTo: fmtDate(dateTo), custCd, deviCd: sendDevi }
    }).then(res => {
        const list = res.data.data || [];
        setOperList([{ CODE_CD: 'ALL' }, ...list]);
    }).catch(console.error);
  }, [deviCd, custCd, dateFrom, dateTo]);

  // 조회 핸들러
  const handleSearch = useCallback(async () => {
    if (!custCd) {
      notify("고객사를 선택하십시오.", "warning", 2000);
      return;
    }
    setLoading(true);

    try {
      const sendDevi = (deviCd === 'ALL' || !deviCd) ? '%' : deviCd;
      const sendOper = (operCd === 'ALL' || !operCd) ? '%' : operCd;

      const params = { 
        custCd, 
        deviCd: sendDevi, 
        operCd: sendOper, 
        dateFrom: fmtDate(dateFrom), 
        dateTo: fmtDate(dateTo), 
        binLimit 
      };

      const res = await axios.get(`${API_URL}/api/oracle/wafer-summary/list`, { params });
      setGridData(res.data.data || []);
      
    } catch (err) {
      notify(err.message, "error", 3000);
    } finally {
      setLoading(false);
    }
  }, [custCd, deviCd, operCd, dateFrom, dateTo, binLimit]);

  const onExporting = (e) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('WaferSummary');
    exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true })
      .then(() => workbook.xlsx.writeBuffer())
      .then(buffer => saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'WaferSummary.xlsx'));
    e.cancel = true;
  };

  const binColumns = useMemo(() => {
    const cols = [];
    for (let i = 1; i <= binLimit; i++) {
      cols.push(
        <Column 
          key={`bin_${i}`} 
          dataField={`bin_${i}`}   
          caption={i.toString()}   
          dataType="number" 
          format="#,##0" 
          width={80} 
          alignment="right"
          headerCellRender={(e) => <div className="bin-cell-header">{e.column.caption}</div>}
        />
      );
    }
    return cols;
  }, [binLimit]);

  return (
    <div className="main-content lot-summary-page">
      <style>{`
        .bin-cell-header { font-size: 11px; text-align: center; font-weight: bold; }
        .dx-radiogroup-horizontal .dx-radiobutton { margin-right: 15px; } 
      `}</style>

      {loading && <div className="loading-overlay"><div className="loading-spinner"></div><div className="loading-text">Loading...</div></div>}

      <div className="toolbar-container" style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px', background: '#fff', padding: '10px', borderBottom: '1px solid #ddd', flexWrap:'wrap' }}>
        <DateBox value={dateFrom} onValueChanged={e => setDateFrom(e.value)} type="date" width={150} displayFormat="yyyy-MM-dd" />
        <DateBox value={dateTo} onValueChanged={e => setDateTo(e.value)} type="date" width={150} displayFormat="yyyy-MM-dd" />
        
        {/* [수정 5] SelectBox 비활성화 조건 추가 */}
        <SelectBox 
            dataSource={custList} 
            value={custCd} 
            valueExpr="CODE_CD" 
            displayExpr="CODE_CD" 
            onValueChanged={e => setCustCd(e.value)} 
            width={180} 
            placeholder="CUST" 
            searchEnabled 
            disabled={isCustFixed} // 고정된 사용자면 disabled
        />
            
        <SelectBox dataSource={deviList} value={deviCd} valueExpr="CODE_CD" displayExpr="CODE_CD" 
            onValueChanged={e => setDeviCd(e.value)} width={200} placeholder="DEVICE" searchEnabled disabled={!custCd} />
            
        <SelectBox dataSource={operList} value={operCd} valueExpr="CODE_CD" displayExpr="CODE_CD" 
            onValueChanged={e => setOperCd(e.value)} width={120} placeholder="OPER" disabled={!custCd} />

        <div style={{ display:'flex', alignItems:'center', border:'1px solid #eee', padding:'0 10px', borderRadius:'4px', background:'#fafafa', height: '32px' }}>
             <span style={{ fontSize: '11px', fontWeight: 'bold', marginRight: '8px', color:'#555' }}>BIN:</span>
             <RadioGroup 
                items={binLimitOptions} 
                value={binLimit} 
                onValueChanged={e => setBinLimit(e.value)} 
                layout="horizontal" 
             />
        </div>

        <Button text="SEARCH" icon="search" type="default" onClick={handleSearch} height={32} />
      </div>

      <div className="section-bar">Wafer Summary</div>
      <div style={{ flex: 1, minHeight: '500px' }}>
        <DataGrid 
            dataSource={gridData} 
            showBorders={true} 
            height="100%" 
            allowColumnResizing={true} 
            rowAlternationEnabled={true} 
            onExporting={onExporting}
        >
          <SearchPanel visible={true} width={240} />
          <Selection mode="single" />
        
          <Export enabled={true} />
          <Paging defaultPageSize={10} />
          
          <Column caption="Wafer Information" isBand={true} fixed={true}>
            <Column dataField="device" caption="DEVICE" width={200} />
            <Column dataField="lot_no" caption="LOT_NO" width={120} />
            <Column dataField="oper" caption="OPER" width={100} alignment="center" />
            <Column dataField="wafer_no" caption="WFR" width={50} alignment="center" />
            <Column dataField="start_time" caption="START" width={150} alignment="center" />
            <Column dataField="end_time" caption="END" width={150} alignment="center" />
            <Column dataField="total_die" caption="IN_QTY" width={80} format="#,##0" />
            <Column dataField="good_die" caption="OUT_QTY" width={80} format="#,##0" />
            <Column dataField="yield" caption="YIELD" width={70} cellRender={e => <span style={{fontWeight:'bold', color: e.value < 90 ? 'red' : 'blue'}}>{e.value}%</span>} />
          </Column>

          <Column caption="BIN Information" isBand={true}>
            {binColumns}
          </Column>

          <Summary>
            <TotalItem column="wafer_no" summaryType="count" displayFormat="Cnt: {0}" />
            <TotalItem column="total_die" summaryType="sum" displayFormat="{0}" />
            <TotalItem column="good_die" summaryType="sum" displayFormat="{0}" />
          </Summary>
        </DataGrid>
      </div>
    </div>
  );
}